import { Box, Popover } from '@material-ui/core';
import React, { useState } from 'react';

import { StreamingResponse } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/components/StreamingResponse';
import { LoanApplicationEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/LoanApplication/LoanApplicationEntity';
import { useErrorResponse } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/hooks/useErrorResponse';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { StatusTagType } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useUser } from '@/hooks/useUser';

interface LoanApplicationResponseCardProps {
  loanApplication: LoanApplicationEntity;
}

export const LoanApplicationResponseCard = ({ loanApplication }: LoanApplicationResponseCardProps) => {
  const { errorResponse, streaming, failed, getErrorResponse } = useErrorResponse({ loanApplication });
  const { getTextIdTranslation } = useTranslation();
  const { userEntity } = useUser();

  const { palette } = useCustomTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const getResponse = async () => {
    setIsLoading(true);
    await getErrorResponse();
    setIsLoading(false);
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    getResponse();
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? `error-response-${loanApplication.id}` : undefined;

  const paragrahps = errorResponse ? [errorResponse.title, errorResponse.description, errorResponse.action] : undefined;

  return (
    <>
      <StatusTag
        type={StatusTagType.WARNING}
        onClick={onClick}
        colorType="dark"
        labelTextId={'NO_RESPONSE'}
        endIcon="info"
        style={{ cursor: 'pointer' }}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          style: {
            width: '300px',
            padding: 24,
          },
        }}
      >
        <Box minHeight={80}>
          <Font variant="body2" display="block" color={'textPrimary'}>
            {failed ? (
              <Text tId="ERROR_FETCHING_ERROR_RESPONSE" />
            ) : (
              <StreamingResponse
                hexColor={palette.text.primary}
                stream={streaming}
                loading={isLoading}
                paragraphs={paragrahps}
                loadingText={
                  <Text
                    tId="ANALYSING_ERROR_RESPONSE"
                    dynamicDataTexts={[getTextIdTranslation(loanApplication.lenderName)]}
                  />
                }
              />
            )}
          </Font>
          {userEntity && userEntity.isRoleAdminOrSuperAdmin() && errorResponse && (
            <>
              <CustomPaper boxProps={{ mt: 2 }} style={{ padding: 16, maxHeight: 320, overflowY: 'scroll' }}>
                <Font variant="body2" display="block" color={'textPrimary'}>
                  <pre style={{ whiteSpace: 'pre-wrap', marginBlock: 0, wordBreak: 'break-all' }}>
                    {errorResponse.message}
                  </pre>
                </Font>
              </CustomPaper>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};
